import { useState, useCallback, useEffect, useRef } from 'react'
import { FiExternalLink, FiBarChart2, FiPieChart, FiTrendingUp, FiDollarSign } from 'react-icons/fi'
import { toast } from 'react-toastify'
import {
  getXeroStatus,
  getXeroReport,
  getSnapshotMetrics,
  getXeroProfitability,
  getNetProfitMargin,
  getOperatingExpenses,
  getFinancialPositionAndCash,
  XERO_ANALYTICS_URL,
  type XeroReportType,
  type NetProfitMarginResponse,
} from '@/services/api/xero'
import FinancePageLoading from '@/components/finance/FinancePageLoading'
import XeroStyleTimeSeriesChart, { type TimeSeriesPoint } from '@/components/finance/XeroStyleTimeSeriesChart'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { ROUTES } from '@/constants/routes'
import { Link, useLocation } from 'react-router-dom'

interface NetIncomePoint {
  month: string
  current: number
  prior: number
}

type NetIncomeGranularity = 'daily' | 'weekly' | 'monthly' | 'quarterly'
type PersistedAnalyticsFilters = {
  granularity: NetIncomeGranularity
  period: string
  year: number
}

const ANALYTICS_FILTERS_STORAGE_KEY = 'finance-analytics:filters'

function getDefaultAnalyticsFilters(): PersistedAnalyticsFilters {
  return {
    granularity: 'monthly',
    period: String(new Date().getMonth()),
    year: new Date().getFullYear(),
  }
}

function sanitizePeriodForGranularity(granularity: NetIncomeGranularity, period: string): string {
  const raw = Number(period)
  if (!Number.isFinite(raw)) {
    return granularity === 'quarterly' ? '1' : String(new Date().getMonth())
  }
  if (granularity === 'quarterly') {
    if (raw >= 1 && raw <= 4) return String(Math.trunc(raw))
    return '1'
  }
  if (raw >= 0 && raw <= 11) return String(Math.trunc(raw))
  return String(new Date().getMonth())
}

function getInitialAnalyticsFilters(): PersistedAnalyticsFilters {
  const defaults = getDefaultAnalyticsFilters()
  if (typeof window === 'undefined') return defaults
  try {
    const raw = window.localStorage.getItem(ANALYTICS_FILTERS_STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<PersistedAnalyticsFilters>
    const granularity: NetIncomeGranularity =
      parsed.granularity === 'daily' ||
      parsed.granularity === 'weekly' ||
      parsed.granularity === 'monthly' ||
      parsed.granularity === 'quarterly'
        ? parsed.granularity
        : defaults.granularity
    const year =
      typeof parsed.year === 'number' && Number.isFinite(parsed.year)
        ? Math.trunc(parsed.year)
        : defaults.year
    const period = sanitizePeriodForGranularity(granularity, String(parsed.period ?? defaults.period))
    return { granularity, period, year }
  } catch {
    return defaults
  }
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const REPORT_OPTIONS: { value: XeroReportType; label: string; icon: React.ReactNode }[] = [
  { value: 'ProfitAndLoss', label: 'Profit & Loss', icon: <FiTrendingUp className="w-4 h-4" /> },
  { value: 'BalanceSheet', label: 'Balance Sheet', icon: <FiBarChart2 className="w-4 h-4" /> },
  { value: 'ExecutiveSummary', label: 'Executive Summary', icon: <FiPieChart className="w-4 h-4" /> },
  { value: 'BankSummary', label: 'Bank Summary', icon: <FiDollarSign className="w-4 h-4" /> },
]

const tabs = [
  { label: 'Overview', path: ROUTES.FINANCE_OVERVIEW },
  { label: 'Invoices', path: ROUTES.FINANCE_INVOICES },
  { label: 'Purchases', path: ROUTES.FINANCE_PURCHASES },
  { label: 'Products and Services', path: ROUTES.FINANCE_PRODUCTS_SERVICES },
  { label: 'Operational Costs', path: ROUTES.FINANCE_OPERATIONAL_COSTS },
  { label: 'Business Snapshot', path: ROUTES.FINANCE_SNAPSHOT },
  { label: 'Business Analytics', path: ROUTES.FINANCE_ANALYTICS },
]

export default function FinanceAnalytics() {
  const location = useLocation()
  const initialFiltersRef = useRef<PersistedAnalyticsFilters>(getInitialAnalyticsFilters())
  const [xeroConnected, setXeroConnected] = useState<boolean | null>(null)
  const [netIncomeGranularity, setNetIncomeGranularity] = useState<NetIncomeGranularity>(
    initialFiltersRef.current.granularity
  )
  const [netIncomePeriod, setNetIncomePeriod] = useState<string>(initialFiltersRef.current.period) // month index or quarter number
  const [netIncomeYear, setNetIncomeYear] = useState<number>(initialFiltersRef.current.year)
  const [selectedReport, setSelectedReport] = useState<XeroReportType | ''>('')
  const [reportData, setReportData] = useState<any>(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [netIncomeData, setNetIncomeData] = useState<NetIncomePoint[] | null>(null)
  const [netIncomeLoading, setNetIncomeLoading] = useState(false)
  const [netIncomeError, setNetIncomeError] = useState<string | null>(null)
  const [netIncomeCurrentTotal, setNetIncomeCurrentTotal] = useState(0)
  const [netIncomePriorTotal, setNetIncomePriorTotal] = useState(0)
  const [netIncomeGrowthPercent, setNetIncomeGrowthPercent] = useState<number | null>(null)
  const [netIncomeRangeLabel, setNetIncomeRangeLabel] = useState('')
  const [netIncomeCurrentMonthRange, setNetIncomeCurrentMonthRange] = useState('')
  const [netIncomePriorMonthRange, setNetIncomePriorMonthRange] = useState('')
  const [totalIncomeData, setTotalIncomeData] = useState<NetIncomePoint[] | null>(null)
  const [totalIncomeCurrentTotal, setTotalIncomeCurrentTotal] = useState(0)
  const [totalIncomePriorTotal, setTotalIncomePriorTotal] = useState(0)
  const [totalIncomeGrowthPercent, setTotalIncomeGrowthPercent] = useState<number | null>(null)
  const [totalExpensesData, setTotalExpensesData] = useState<NetIncomePoint[] | null>(null)
  const [totalExpensesCurrentTotal, setTotalExpensesCurrentTotal] = useState(0)
  const [totalExpensesPriorTotal, setTotalExpensesPriorTotal] = useState(0)
  const [totalExpensesGrowthPercent, setTotalExpensesGrowthPercent] = useState<number | null>(null)
  const [netProfitMarginResult, setNetProfitMarginResult] = useState<NetProfitMarginResponse | null>(null)
  const [netProfitMarginData, setNetProfitMarginData] = useState<TimeSeriesPoint[] | null>(null)
  const [grossProfitMarginData, setGrossProfitMarginData] = useState<TimeSeriesPoint[] | null>(null)
  const [grossProfitMarginCurrent, setGrossProfitMarginCurrent] = useState(0)
  const [grossProfitMarginPrior, setGrossProfitMarginPrior] = useState(0)
  const [grossProfitMarginGrowthPercent, setGrossProfitMarginGrowthPercent] = useState<number | null>(null)
  const [operatingExpensesTotal, setOperatingExpensesTotal] = useState(0)
  const [operatingExpensesLabel, setOperatingExpensesLabel] = useState('')
  const [operatingExpensesData, setOperatingExpensesData] = useState<{ name: string; value: number }[]>([])
  const [bankAccountsTotal, setBankAccountsTotal] = useState(0)
  const [bankAccountsLabel, setBankAccountsLabel] = useState('')
  const [bankAccountsData, setBankAccountsData] = useState<{ name: string; value: number }[]>([])

  const checkXero = useCallback(() => {
    getXeroStatus()
      .then((s) => setXeroConnected(s?.connected ?? false))
      .catch(() => {
        // Don't auto-disconnect on network/API error – assume still connected
        setXeroConnected(true)
      })
  }, [])

  useEffect(() => {
    checkXero()
  }, [checkXero])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const payload: PersistedAnalyticsFilters = {
        granularity: netIncomeGranularity,
        period: sanitizePeriodForGranularity(netIncomeGranularity, netIncomePeriod),
        year: netIncomeYear,
      }
      window.localStorage.setItem(ANALYTICS_FILTERS_STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // Ignore storage errors so analytics remains usable in restricted browsers.
    }
  }, [netIncomeGranularity, netIncomePeriod, netIncomeYear])

  useEffect(() => {
    if (!xeroConnected) return

    const pad = (n: number) => String(n).padStart(2, '0')
    const now = new Date()

    const getRangeFromFilters = (
      granularity: NetIncomeGranularity,
      period: string,
      year: number
    ): { currentStart: string; currentEnd: string; priorStart: string; priorEnd: string; groupBy: 'day' | 'month' } => {
      const y = year

      // Monthly view: trailing 12‑month window ending at the *end* of the selected month.
      if (granularity === 'monthly') {
        const rawIndex = Number(period)
        const safeMonth = Number.isFinite(rawIndex) ? rawIndex : now.getMonth()

        // Current end: last day of selected month/year (e.g. 31 Mar 2026)
        const endDate = new Date(y, safeMonth + 1, 0)
        const currentEnd = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`

        // Current start: first day of month 11 months earlier (e.g. 1 Apr 2025).
        // Set day to 1 first so "31 Mar -> -11 months" doesn't roll to 1 May (31 Apr invalid).
        const startDate = new Date(endDate)
        startDate.setDate(1)
        startDate.setMonth(startDate.getMonth() - 11)
        const currentStart = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-01`

        // Prior period: same 12‑month window one year earlier (e.g. Apr 2024 – Mar 2025)
        const priorStartDate = new Date(startDate)
        priorStartDate.setFullYear(priorStartDate.getFullYear() - 1)
        const priorStart = `${priorStartDate.getFullYear()}-${pad(priorStartDate.getMonth() + 1)}-01`

        const priorEndDate = new Date(endDate)
        priorEndDate.setFullYear(priorEndDate.getFullYear() - 1)
        const priorEnd = `${priorEndDate.getFullYear()}-${pad(priorEndDate.getMonth() + 1)}-${pad(priorEndDate.getDate())}`

        return { currentStart, currentEnd, priorStart, priorEnd, groupBy: 'month' }
      }

      if (granularity === 'quarterly') {
        const quarter = Number(period) || Math.floor(now.getMonth() / 3) + 1
        const startMonth = (quarter - 1) * 3
        const endMonth = startMonth + 2
        const start = `${y}-${pad(startMonth + 1)}-01`
        const endDate = new Date(y, endMonth + 1, 0)
        const end = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
        const priorStartYear = y - 1
        const priorEndYear = y - 1
        const priorStart = `${priorStartYear}-${pad(startMonth + 1)}-01`
        const priorEndDate = new Date(priorEndYear, endMonth + 1, 0)
        const priorEnd = `${priorEndDate.getFullYear()}-${pad(priorEndDate.getMonth() + 1)}-${pad(priorEndDate.getDate())}`
        return { currentStart: start, currentEnd: end, priorStart, priorEnd, groupBy: 'month' }
      }

      // Default for now (daily/weekly): single calendar month window around the selected month.
      const monthIndex = Number(period)
      const safeMonth = Number.isFinite(monthIndex) ? monthIndex : now.getMonth()
      const start = `${y}-${pad(safeMonth + 1)}-01`
      const endDate = new Date(y, safeMonth + 1, 0)
      const end = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
      const priorStartYear = y - 1
      const priorEndYear = y - 1
      const priorStart = `${priorStartYear}-${pad(safeMonth + 1)}-01`
      const priorEndDate = new Date(priorEndYear, safeMonth + 1, 0)
      const priorEnd = `${priorEndDate.getFullYear()}-${pad(priorEndDate.getMonth() + 1)}-${pad(priorEndDate.getDate())}`
      return { currentStart: start, currentEnd: end, priorStart, priorEnd, groupBy: 'month' }
    }

    const { currentStart, currentEnd, priorStart, priorEnd, groupBy } = getRangeFromFilters(
      netIncomeGranularity,
      netIncomePeriod,
      netIncomeYear
    )

    // Operating expenses donut should reflect the selected filter period itself
    // (e.g. Jan 2026 only), not the trailing 12-month window used by margin cards.
    const getOperatingExpensesRangeFromFilters = (
      granularity: NetIncomeGranularity,
      period: string,
      year: number
    ): { start: string; end: string } => {
      const y = year
      const monthIndex = Number(period)
      const safeMonth = Number.isFinite(monthIndex) ? monthIndex : now.getMonth()

      if (granularity === 'quarterly') {
        const quarter = Number(period) || Math.floor(now.getMonth() / 3) + 1
        const startMonth = (quarter - 1) * 3
        const endMonth = startMonth + 2
        const start = `${y}-${pad(startMonth + 1)}-01`
        const endDate = new Date(y, endMonth + 1, 0)
        const end = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
        return { start, end }
      }

      const start = `${y}-${pad(safeMonth + 1)}-01`
      const endDate = new Date(y, safeMonth + 1, 0)
      const end = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
      return { start, end }
    }

    const { start: operatingStart, end: operatingEnd } = getOperatingExpensesRangeFromFilters(
      netIncomeGranularity,
      netIncomePeriod,
      netIncomeYear
    )

    const formatRangeLabel = (from: string, to: string): string => {
      if (!from || !to) return ''
      const d1 = new Date(from)
      const d2 = new Date(to)
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ''
      const fmt = (d: Date) =>
        d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(d1)} – ${fmt(d2)}`
    }

    const formatMonthRangeLabel = (from: string, to: string): string => {
      if (!from || !to) return ''
      const d1 = new Date(from)
      const d2 = new Date(to)
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ''
      const fmt = (d: Date) =>
        d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
      return `${fmt(d1)} to ${fmt(d2)}`
    }
    const formatSelectedPeriodLabel = (from: string, to: string): string => {
      if (!from || !to) return ''
      const d1 = new Date(from)
      const d2 = new Date(to)
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return ''
      const sameMonth =
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth()
      if (sameMonth) {
        return d1.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
      }
      return formatMonthRangeLabel(from, to)
    }

    setNetIncomeLoading(true)
    setNetIncomeError(null)
    setNetIncomeData(null)

    Promise.all([
      getSnapshotMetrics(currentStart, currentEnd, groupBy),
      getSnapshotMetrics(priorStart, priorEnd, groupBy),
      getXeroProfitability(currentStart, currentEnd),
      getNetProfitMargin(currentStart, currentEnd).catch(() => null),
      getOperatingExpenses({ fromDate: operatingStart, toDate: operatingEnd }).catch(() => null),
      getFinancialPositionAndCash(operatingEnd, operatingStart).catch(() => null),
    ])
      .then(([currentRes, priorRes, profitability, netProfitMargin, operatingExpenses, financialPositionCash]) => {
        const currentMetrics = currentRes?.metrics ?? []
        const priorMetrics = priorRes?.metrics ?? []
        const length = Math.max(currentMetrics.length, priorMetrics.length)
        const netPoints: NetIncomePoint[] = []
        const incomePoints: NetIncomePoint[] = []
        const expensesPoints: NetIncomePoint[] = []
        const grossMarginPoints: TimeSeriesPoint[] = []

        const formatLabel = (dateStr: string): string => {
          const d = new Date(dateStr)
          if (isNaN(d.getTime())) return dateStr
          if (groupBy === 'day') {
            return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          }
          return d.toLocaleDateString('en-GB', { month: 'short' })
        }
        const formatPointDate = (dateStr: string): string => {
          const d = new Date(dateStr)
          if (isNaN(d.getTime())) return dateStr
          return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        }
        const shiftOneYearBack = (dateStr: string): string => {
          const d = new Date(dateStr)
          if (isNaN(d.getTime())) return dateStr
          d.setFullYear(d.getFullYear() - 1)
          return d.toISOString().split('T')[0]
        }

        for (let i = 0; i < length; i++) {
          const cur = currentMetrics[i]
          const prev = priorMetrics[i]
          if (!cur && !prev) continue
          const source = cur ?? prev
          const currentGrossMargin =
            typeof cur?.grossMargin === 'number'
              ? cur.grossMargin
              : cur?.revenue
                ? (((cur.revenue ?? 0) - (cur.cogs ?? 0)) / cur.revenue) * 100
                : 0
          const priorGrossMargin =
            typeof prev?.grossMargin === 'number'
              ? prev.grossMargin
              : prev?.revenue
                ? (((prev.revenue ?? 0) - (prev.cogs ?? 0)) / prev.revenue) * 100
                : 0
          const monthLabel = formatLabel(source!.date)
          netPoints.push({
            month: monthLabel,
            current: cur?.netProfit ?? 0,
            prior: prev?.netProfit ?? 0,
          })
          incomePoints.push({
            month: monthLabel,
            current: cur?.revenue ?? 0,
            prior: prev?.revenue ?? 0,
          })
          // Total expenses = COGS + operating expenses so card total matches P&L identity (revenue - net profit).
          const curTotalExp = (cur?.cogs ?? 0) + (cur?.expenses ?? 0)
          const prevTotalExp = (prev?.cogs ?? 0) + (prev?.expenses ?? 0)
          expensesPoints.push({
            month: monthLabel,
            current: curTotalExp,
            prior: prevTotalExp,
          })
          grossMarginPoints.push({
            month: monthLabel,
            current: currentGrossMargin,
            previous: priorGrossMargin,
            currentDate: formatPointDate(source!.date),
            previousDate: formatPointDate(prev?.date ?? shiftOneYearBack(source!.date)),
          })
        }
        grossMarginPoints.sort((a, b) => {
          const t1 = a.currentDate ? new Date(a.currentDate).getTime() : 0
          const t2 = b.currentDate ? new Date(b.currentDate).getTime() : 0
          return t1 - t2
        })

        if (!netPoints.length) {
          setNetIncomeData([])
          setNetIncomeCurrentTotal(0)
          setNetIncomePriorTotal(0)
          setNetIncomeGrowthPercent(null)
          setNetIncomeRangeLabel(formatRangeLabel(currentStart, currentEnd))
          setNetIncomeCurrentMonthRange(formatMonthRangeLabel(currentStart, currentEnd))
          setNetIncomePriorMonthRange(formatMonthRangeLabel(priorStart, priorEnd))
          setTotalIncomeData([])
          setTotalIncomeCurrentTotal(0)
          setTotalIncomePriorTotal(0)
          setTotalIncomeGrowthPercent(null)
          setTotalExpensesData([])
          setTotalExpensesCurrentTotal(0)
          setTotalExpensesPriorTotal(0)
          setTotalExpensesGrowthPercent(null)
          setNetProfitMarginData([])
          setGrossProfitMarginData([])
          setGrossProfitMarginCurrent(0)
          setGrossProfitMarginPrior(0)
          setGrossProfitMarginGrowthPercent(null)
          setOperatingExpensesTotal(0)
          setOperatingExpensesLabel('')
          setOperatingExpensesData([])
          setBankAccountsTotal(0)
          setBankAccountsLabel('')
          setBankAccountsData([])
          return
        }

        const netCurrentSeriesTotal = netPoints.reduce((sum, p) => sum + p.current, 0)
        const netPriorSeriesTotal = netPoints.reduce((sum, p) => sum + p.prior, 0)

        const incomeCurrentSeriesTotal = incomePoints.reduce((sum, p) => sum + p.current, 0)
        const incomePriorSeriesTotal = incomePoints.reduce((sum, p) => sum + p.prior, 0)

        const expensesCurrentSeriesTotal = expensesPoints.reduce((sum, p) => sum + p.current, 0)
        const expensesPriorSeriesTotal = expensesPoints.reduce((sum, p) => sum + p.prior, 0)

        // Prefer Xero Profit & Loss–based totals (currentProfit / previousProfit) when available,
        // to mirror Business Snapshot widget; fall back to summed series otherwise.
        const currentTotal =
          profitability && typeof (profitability as any).currentProfit === 'number'
            ? (profitability as any).currentProfit
            : netCurrentSeriesTotal
        const priorTotal =
          profitability && typeof (profitability as any).previousProfit === 'number'
            ? (profitability as any).previousProfit
            : netPriorSeriesTotal

        const growth =
          priorTotal === 0
            ? currentTotal > 0
              ? 100
              : 0
            : ((currentTotal - priorTotal) / Math.abs(priorTotal)) * 100

        const incomeCurrentTotal =
          profitability && typeof (profitability as any).currentSalesRevenue === 'number'
            ? (profitability as any).currentSalesRevenue
            : incomeCurrentSeriesTotal
        const incomePriorTotal =
          profitability && typeof (profitability as any).previousSalesRevenue === 'number'
            ? (profitability as any).previousSalesRevenue
            : incomePriorSeriesTotal

        const incomeGrowth =
          incomePriorTotal === 0
            ? incomeCurrentTotal > 0
              ? 100
              : 0
            : ((incomeCurrentTotal - incomePriorTotal) / Math.abs(incomePriorTotal)) * 100

        // Total expenses card should mirror Xero Business Snapshot period totals.
        // Prefer profitability totals (revenue - net profit) for the selected period window.
        // Fall back to summed series only if profitability values are unavailable.
        const expensesCurrentTotal =
          profitability &&
          typeof (profitability as any).currentSalesRevenue === 'number' &&
          typeof (profitability as any).currentProfit === 'number'
            ? (profitability as any).currentSalesRevenue - (profitability as any).currentProfit
            : expensesPoints.length > 0
              ? expensesCurrentSeriesTotal
              : 0
        const expensesPriorTotal =
          profitability &&
          typeof (profitability as any).previousSalesRevenue === 'number' &&
          typeof (profitability as any).previousProfit === 'number'
            ? (profitability as any).previousSalesRevenue - (profitability as any).previousProfit
            : expensesPoints.length > 0
              ? expensesPriorSeriesTotal
              : 0

        const expensesGrowth =
          expensesPriorTotal === 0
            ? expensesCurrentTotal > 0
              ? 100
              : 0
            : ((expensesCurrentTotal - expensesPriorTotal) / Math.abs(expensesPriorTotal)) * 100

        const profitabilityCurrentRevenue =
          profitability && typeof (profitability as any).currentSalesRevenue === 'number'
            ? (profitability as any).currentSalesRevenue
            : null
        const profitabilityPriorRevenue =
          profitability && typeof (profitability as any).previousSalesRevenue === 'number'
            ? (profitability as any).previousSalesRevenue
            : null
        const profitabilityCurrentCogs =
          profitability && typeof (profitability as any).currentCogs === 'number'
            ? (profitability as any).currentCogs
            : null
        const profitabilityPriorCogs =
          profitability && typeof (profitability as any).previousCogs === 'number'
            ? (profitability as any).previousCogs
            : null

        const grossMarginCurrentFromProfitability =
          profitabilityCurrentRevenue != null &&
          profitabilityCurrentCogs != null &&
          profitabilityCurrentRevenue !== 0
            ? (((profitabilityCurrentRevenue - profitabilityCurrentCogs) / profitabilityCurrentRevenue) * 100)
            : null
        const grossMarginPriorFromProfitability =
          profitabilityPriorRevenue != null &&
          profitabilityPriorCogs != null &&
          profitabilityPriorRevenue !== 0
            ? (((profitabilityPriorRevenue - profitabilityPriorCogs) / profitabilityPriorRevenue) * 100)
            : null

        // Fallback: compute weighted margin over the selected period (sum gross profit / sum revenue),
        // instead of simple averaging of monthly percentages.
        const grossMarginCurrentWeighted = (() => {
          const currentRevenue = incomePoints.reduce((sum, point) => sum + (point.current || 0), 0)
          const currentCogs = currentMetrics.reduce((sum: number, metric: any) => sum + (metric?.cogs || 0), 0)
          if (!currentRevenue) return 0
          return ((currentRevenue - currentCogs) / currentRevenue) * 100
        })()
        const grossMarginPriorWeighted = (() => {
          const priorRevenue = incomePoints.reduce((sum, point) => sum + (point.prior || 0), 0)
          const priorCogs = priorMetrics.reduce((sum: number, metric: any) => sum + (metric?.cogs || 0), 0)
          if (!priorRevenue) return 0
          return ((priorRevenue - priorCogs) / priorRevenue) * 100
        })()

        const grossMarginCurrent =
          grossMarginCurrentFromProfitability != null
            ? grossMarginCurrentFromProfitability
            : grossMarginCurrentWeighted
        const grossMarginPrior =
          grossMarginPriorFromProfitability != null
            ? grossMarginPriorFromProfitability
            : grossMarginPriorWeighted
        const grossMarginGrowth = grossMarginCurrent - grossMarginPrior

        const netProfitMarginPoints: TimeSeriesPoint[] =
          netProfitMargin?.labels?.map((label, index) => {
            const pointDate = new Date(currentStart)
            pointDate.setMonth(pointDate.getMonth() + index)
            const previousPointDate = new Date(priorStart)
            previousPointDate.setMonth(previousPointDate.getMonth() + index)
            return {
              month: label,
              current: netProfitMargin.currentSeries[index] ?? 0,
              previous: netProfitMargin.previousSeries[index] ?? 0,
              currentDate: pointDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
              previousDate: previousPointDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
            }
          }) ?? []

        const operatingItems = operatingExpenses?.items ?? []
        const operatingTotal = operatingExpenses?.total ?? 0
        const topItems = operatingItems.slice(0, 4)
        const topItemsTotal = topItems.reduce((sum, item) => sum + item.value, 0)
        // Keep signed "Other" so breakdown reconciles exactly to reported operating total.
        const otherValue = operatingTotal - topItemsTotal
        const donutData =
          otherValue !== 0
            ? [...topItems, { name: 'Other', value: otherValue }]
            : topItems
        const bankAccounts = financialPositionCash?.cashBalance?.accounts ?? []
        const bankAccountsTotal = financialPositionCash?.cashBalance?.total ?? 0
        const topBankAccounts = [...bankAccounts]
          .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
          .slice(0, 4)
          .map((account) => ({ name: account.name, value: account.balance }))
        const topBankAccountsTotal = topBankAccounts.reduce((sum, item) => sum + item.value, 0)
        const otherBankAccountsValue = bankAccountsTotal - topBankAccountsTotal
        const bankDonutData =
          otherBankAccountsValue !== 0
            ? [...topBankAccounts, { name: 'Other', value: otherBankAccountsValue }]
            : topBankAccounts

        setNetIncomeData(netPoints)
        setNetIncomeCurrentTotal(currentTotal)
        setNetIncomePriorTotal(priorTotal)
        setNetIncomeGrowthPercent(growth)
        setNetIncomeRangeLabel(formatRangeLabel(currentStart, currentEnd))
        setNetIncomeCurrentMonthRange(formatMonthRangeLabel(currentStart, currentEnd))
        setNetIncomePriorMonthRange(formatMonthRangeLabel(priorStart, priorEnd))
        setTotalIncomeData(incomePoints)
        setTotalIncomeCurrentTotal(incomeCurrentTotal)
        setTotalIncomePriorTotal(incomePriorTotal)
        setTotalIncomeGrowthPercent(incomeGrowth)
        setTotalExpensesData(expensesPoints)
        setTotalExpensesCurrentTotal(expensesCurrentTotal)
        setTotalExpensesPriorTotal(expensesPriorTotal)
        setTotalExpensesGrowthPercent(expensesGrowth)
        setNetProfitMarginResult(netProfitMargin)
        setNetProfitMarginData(netProfitMarginPoints)
        setGrossProfitMarginData(grossMarginPoints)
        setGrossProfitMarginCurrent(grossMarginCurrent)
        setGrossProfitMarginPrior(grossMarginPrior)
        setGrossProfitMarginGrowthPercent(grossMarginGrowth)
        setOperatingExpensesTotal(operatingTotal)
        setOperatingExpensesLabel(
          operatingExpenses?.currentLabel ? `Accounts total ${operatingExpenses.currentLabel}` : ''
        )
        setOperatingExpensesData(donutData)
        setBankAccountsTotal(bankAccountsTotal)
        setBankAccountsLabel(`Accounts total ${formatSelectedPeriodLabel(operatingStart, operatingEnd)}`)
        setBankAccountsData(bankDonutData)
      })
      .catch((e: any) => {
        const msg =
          e?.response?.data?.error?.message || e?.message || 'Failed to load net income analytics from Xero.'
        setNetIncomeError(msg)
        setNetIncomeData(null)
        setTotalIncomeData(null)
        setTotalExpensesData(null)
        setNetProfitMarginResult(null)
        setNetProfitMarginData(null)
        setGrossProfitMarginData(null)
        setOperatingExpensesTotal(0)
        setOperatingExpensesLabel('')
        setOperatingExpensesData([])
        setBankAccountsTotal(0)
        setBankAccountsLabel('')
        setBankAccountsData([])
        toast.error(msg)
      })
      .finally(() => {
        setNetIncomeLoading(false)
      })
  }, [xeroConnected, netIncomeGranularity, netIncomePeriod, netIncomeYear])

  const loadReport = async () => {
    if (!selectedReport) return
    setReportLoading(true)
    setReportData(null)
    try {
      const params: { fromDate?: string; toDate?: string } = {}
      if (fromDate) params.fromDate = fromDate
      if (toDate) params.toDate = toDate
      const res = await getXeroReport(selectedReport, Object.keys(params).length ? params : undefined)
      setReportData(res?.report ?? null)
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to load report.')
    } finally {
      setReportLoading(false)
    }
  }

  const flattenRows = (rows: any[]): any[] => {
    const out: any[] = []
    for (const r of rows || []) {
      if (r.RowType === 'Row' || r.RowType === 'SummaryRow') {
        out.push(r)
      } else if (r.RowType === 'Section' && r.Rows?.length) {
        out.push(...flattenRows(r.Rows))
      }
    }
    return out
  }

  const renderReportTable = (report: any) => {
    if (!report || !report.Rows || !Array.isArray(report.Rows)) return null
    const headerRow = report.Rows.find((r: any) => r.RowType === 'Header')
    const bodyRows = flattenRows(report.Rows)
    if (!headerRow?.Cells?.length && !bodyRows.length) return null
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 mt-4">
        <table className="min-w-full divide-y divide-gray-200" style={{ fontFamily: 'Lexend Deca', fontSize: '13px' }}>
          {headerRow?.Cells?.length > 0 && (
            <thead className="bg-gray-50">
              <tr>
                {headerRow.Cells.map((c: any, i: number) => (
                  <th key={i} className="px-4 py-2 text-left font-medium text-gray-700">
                    {c.Value ?? ''}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="bg-white divide-y divide-gray-200">
            {bodyRows.map((row: any, ri: number) => (
              <tr
                key={ri}
                className={row.RowType === 'SummaryRow' ? 'bg-gray-50 font-medium' : ''}
              >
                {row.Cells?.map((c: any, ci: number) => (
                  <td key={ci} className="px-4 py-2 text-gray-800">
                    {c.Value ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderReportFields = (report: any) => {
    if (!report || !report.Fields || !Array.isArray(report.Fields)) return null
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {report.Fields.map((f: any, i: number) => (
          <div key={i} className="flex justify-between border-b border-gray-100 py-2">
            <span className="text-gray-600">{f.Description ?? f.FieldID}</span>
            <span className="font-medium">{f.Value ?? ''}</span>
          </div>
        ))}
      </div>
    )
  }

  const formatNetIncomeAmount = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Math.round(value || 0))
  }

  const netIncomeAllValues =
    netIncomeData?.flatMap((p) => [p.current, p.prior]) ?? []
  const netIncomeAbsMax = netIncomeAllValues.length
    ? Math.max(...netIncomeAllValues.map((v) => Math.abs(v)), 1)
    : 0
  const netIncomePadded = netIncomeAbsMax * 1.15 || 1000
  const netIncomeDomain: [number, number] = [-netIncomePadded, netIncomePadded]

  const totalIncomeAllValues =
    totalIncomeData?.flatMap((p) => [p.current, p.prior]) ?? []
  const totalIncomeAbsMax = totalIncomeAllValues.length
    ? Math.max(...totalIncomeAllValues.map((v) => Math.abs(v)), 1)
    : 0
  const totalIncomePadded = totalIncomeAbsMax * 1.15 || 1000
  const totalIncomeDomain: [number, number] = [-totalIncomePadded, totalIncomePadded]

  const totalExpensesAllValues =
    totalExpensesData?.flatMap((p) => [p.current, p.prior]) ?? []
  const totalExpensesAbsMax = totalExpensesAllValues.length
    ? Math.max(...totalExpensesAllValues.map((v) => Math.abs(v)), 1)
    : 0
  const totalExpensesPadded = totalExpensesAbsMax * 1.15 || 1000
  const totalExpensesDomain: [number, number] = [0, totalExpensesPadded]

  const roundToNice = (n: number) => {
    if (!Number.isFinite(n) || n <= 0) return 10
    const magnitude = Math.pow(10, Math.floor(Math.log10(n)))
    return Math.ceil(n / magnitude) * magnitude
  }
  const netProfitMarginAllValues =
    netProfitMarginData?.flatMap((p) => [p.current, p.previous]).filter((v) => Number.isFinite(v)) ?? []
  const netProfitMarginAbsMax = netProfitMarginAllValues.length
    ? Math.max(...netProfitMarginAllValues.map((v) => Math.abs(v)), 1)
    : 10
  const netProfitMarginDomain: [number, number] = [-roundToNice(netProfitMarginAbsMax * 1.15), roundToNice(netProfitMarginAbsMax * 1.15)]
  // Derive display values for net income margin from backend result,
  // but correct the sign if the chart series clearly indicates a net loss.
  let netProfitMarginDisplayCurrent = netProfitMarginResult?.currentMargin ?? 0
  let netProfitMarginDisplayPrevious = netProfitMarginResult?.previousMargin ?? 0
  let netProfitMarginDisplayChange = netProfitMarginResult?.marginChangePercent ?? 0
  if (netProfitMarginResult && netProfitMarginData && netProfitMarginData.length > 0) {
    const avgCurrentSeries =
      netProfitMarginData.reduce((sum, p) => sum + (Number.isFinite(p.current) ? p.current : 0), 0) /
      netProfitMarginData.length
    const avgPreviousSeries =
      netProfitMarginData.reduce((sum, p) => sum + (Number.isFinite(p.previous) ? p.previous : 0), 0) /
      netProfitMarginData.length
    const currentSignMismatch = avgCurrentSeries < 0 && netProfitMarginDisplayCurrent > 0
    const previousSignMismatch = avgPreviousSeries < 0 && netProfitMarginDisplayPrevious > 0
    if (currentSignMismatch) netProfitMarginDisplayCurrent = -netProfitMarginDisplayCurrent
    if (previousSignMismatch) netProfitMarginDisplayPrevious = -netProfitMarginDisplayPrevious
    if (currentSignMismatch || previousSignMismatch) {
      netProfitMarginDisplayChange =
        netProfitMarginDisplayPrevious === 0
          ? 0
          : ((netProfitMarginDisplayCurrent - netProfitMarginDisplayPrevious) /
              Math.abs(netProfitMarginDisplayPrevious)) *
            100
    }
  }

  const grossProfitMarginAllValues =
    grossProfitMarginData?.flatMap((p) => [p.current, p.previous]).filter((v) => Number.isFinite(v)) ?? []
  const grossProfitMarginAbsMax = grossProfitMarginAllValues.length
    ? Math.max(...grossProfitMarginAllValues.map((v) => Math.abs(v)), 1)
    : 10
  const grossProfitMarginDomain: [number, number] = [-roundToNice(grossProfitMarginAbsMax * 1.15), roundToNice(grossProfitMarginAbsMax * 1.15)]
  const profitLossChartData =
    (netIncomeData ?? []).map((point) => ({
      month: point.month,
      profit: Math.max(point.current ?? 0, 0),
      loss: Math.min(point.current ?? 0, 0),
      net: point.current ?? 0,
    })) || []
  // Use the same authoritative current-period total shown in Net income card
  // (Profitability API when available), instead of summing plotted points.
  const profitLossTotal = netIncomeCurrentTotal
  const profitLossAllValues = profitLossChartData.flatMap((point) => [point.profit ?? 0, point.loss ?? 0])
  const profitLossAbsMax = profitLossAllValues.length
    ? Math.max(...profitLossAllValues.map((value) => Math.abs(value)), 1)
    : 1
  const profitLossDomain: [number, number] = [-roundToNice(profitLossAbsMax * 1.15), roundToNice(profitLossAbsMax * 1.15)]
  const formatMillionsTick = (value: number): string => {
    if (value === 0) return '0'
    const abs = Math.abs(value)
    if (abs >= 1_000_000) return `${Math.round(value / 1_000_000)}M`
    if (abs >= 1_000) return `${Math.round(value / 1_000)}K`
    return `${Math.round(value)}`
  }
  const operatingExpenseColors = ['#0052CC', '#9BD5FF', '#1E88E5', '#8E24AA', '#2E4A8A']
  const bankAccountsColors = ['#0F766E', '#2DD4BF', '#14B8A6', '#0EA5E9', '#334155']
  const operatingExpensesTooltipLabel = operatingExpensesLabel.replace(/^Accounts total\s*/i, '') || 'Selected period'
  const bankAccountsTooltipLabel = bankAccountsLabel.replace(/^Accounts total\s*/i, '') || 'Selected period'
  const renderOperatingExpensesTooltip = ({ active }: any) => {
    if (!active || !operatingExpensesData.length) return null
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: '10px 12px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          fontFamily: 'Lexend Deca',
          fontSize: 13,
          minWidth: 220,
        }}
      >
        <div style={{ color: '#6B7280', marginBottom: 8 }}>{operatingExpensesTooltipLabel}</div>
        {operatingExpensesData.map((item, idx) => {
          const pct = operatingExpensesTotal !== 0 ? (item.value / operatingExpensesTotal) * 100 : 0
          return (
            <div
              key={item.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: idx === 0 ? 0 : 6,
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: operatingExpenseColors[idx % operatingExpenseColors.length],
                  display: 'inline-block',
                }}
              />
              <span style={{ color: '#111827', flex: 1 }}>{item.name}</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{formatNetIncomeAmount(item.value)}</span>
              <span
                style={{
                  color: '#6B7280',
                  fontSize: 12,
                  background: '#F3F4F6',
                  borderRadius: 999,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {pct.toFixed(2)}% of total
              </span>
            </div>
          )
        })}
      </div>
    )
  }
  const renderBankAccountsTooltip = ({ active }: any) => {
    if (!active || !bankAccountsData.length) return null
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: '10px 12px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          fontFamily: 'Lexend Deca',
          fontSize: 13,
          minWidth: 220,
        }}
      >
        <div style={{ color: '#6B7280', marginBottom: 8 }}>{bankAccountsTooltipLabel}</div>
        {bankAccountsData.map((item, idx) => {
          const pct = bankAccountsTotal !== 0 ? (item.value / bankAccountsTotal) * 100 : 0
          return (
            <div
              key={item.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: idx === 0 ? 0 : 6,
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: bankAccountsColors[idx % bankAccountsColors.length],
                  display: 'inline-block',
                }}
              />
              <span style={{ color: '#111827', flex: 1 }}>{item.name}</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{formatNetIncomeAmount(item.value)}</span>
              <span
                style={{
                  color: '#6B7280',
                  fontSize: 12,
                  background: '#F3F4F6',
                  borderRadius: 999,
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {pct.toFixed(2)}% of total
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6" style={{ fontFamily: 'Lexend Deca', maxWidth: '100%' }}>
      {/* Header: title + breadcrumbs */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              color: '#212126',
            }}
          >
            Business Analytics
          </h1>
          <span
            className="text-xs sm:text-sm"
            style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Business Analytics</span>
          </span>
        </div>

        {/* Tabs - full width so all tabs are visible */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px',
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <p className="mt-1 text-sm text-gray-500">
          Xero Business Analytics is powered by Syft. Open Xero to use full Syft dashboards, or view key reports below from your connected Xero organisation.
        </p>
      </div>

      {xeroConnected === null && (
        <FinancePageLoading message="Checking Xero connection…" />
      )}

      {xeroConnected === false && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6">
          <p className="text-amber-800 text-sm">
            Xero is not connected. Connect Xero in{' '}
            <Link to={ROUTES.SETTINGS_INTEGRATIONS} className="underline font-medium">
              Settings → Integrations
            </Link>{' '}
            to open Analytics in Xero and load reports here.
          </p>
        </div>
      )}

      {xeroConnected && netIncomeLoading && (
        <FinancePageLoading message="Loading Business Analytics…" />
      )}

      {xeroConnected && !netIncomeLoading && (
      <>
      {/* Net income comparison – current FY vs prior FY, clustered bars around zero */}
      {xeroConnected && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-sm font-medium text-gray-900 mb-1">Net income</h2>
                <p className="text-xs text-gray-500">
                  {netIncomeRangeLabel || 'Select a period to view current vs prior year.'}
                </p>
              </div>
              {/* Filters: frequency (granularity), period (month/quarter), year */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Granularity */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                  <select
                    value={netIncomeGranularity}
                    onChange={(e) => {
                      const next = e.target.value as NetIncomeGranularity
                      setNetIncomeGranularity(next)
                      // Reset period to sensible default when switching modes
                      if (next === 'quarterly') {
                        const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1
                        setNetIncomePeriod(String(currentQuarter))
                      } else {
                        setNetIncomePeriod(String(new Date().getMonth()))
                      }
                    }}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-xs md:text-sm min-w-[120px]"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
                {/* Period (month or quarter) */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {netIncomeGranularity === 'quarterly' ? 'Quarter' : 'Month'}
                  </label>
                  <select
                    value={netIncomePeriod}
                    onChange={(e) => setNetIncomePeriod(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-xs md:text-sm min-w-[100px]"
                  >
                    {netIncomeGranularity === 'quarterly'
                      ? [
                          { value: '1', label: 'Q1' },
                          { value: '2', label: 'Q2' },
                          { value: '3', label: 'Q3' },
                          { value: '4', label: 'Q4' },
                        ].map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))
                      : MONTH_LABELS.map((label, index) => (
                          <option key={index} value={String(index)}>
                            {label}
                          </option>
                        ))}
                  </select>
                </div>
                {/* Year */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                  <select
                    value={netIncomeYear}
                    onChange={(e) => setNetIncomeYear(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-xs md:text-sm min-w-[80px]"
                  >
                    {Array.from({ length: 5 }).map((_, idx) => {
                      const y = new Date().getFullYear() - 2 + idx
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-8">
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Current period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {netIncomeData && !netIncomeLoading
                    ? formatNetIncomeAmount(netIncomeCurrentTotal)
                    : '—'}
                </span>
                {netIncomeCurrentMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomeCurrentMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Prior period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {netIncomeData && !netIncomeLoading
                    ? formatNetIncomeAmount(netIncomePriorTotal)
                    : '—'}
                </span>
                {netIncomePriorMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomePriorMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col justify-center min-w-[140px]">
                {netIncomeError ? (
                  <span className="text-xs text-red-600">{netIncomeError}</span>
                ) : netIncomeLoading ? (
                  <span className="text-xs text-gray-500">Loading…</span>
                ) : netIncomeGrowthPercent != null && netIncomePriorTotal !== 0 ? (
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: netIncomeGrowthPercent >= 0 ? '#13AB5B' : '#E93323',
                      fontFamily: 'Lexend Deca',
                    }}
                  >
                    {netIncomeGrowthPercent >= 0 ? 'Up' : 'Down'}{' '}
                    {Math.abs(netIncomeGrowthPercent).toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">
                    No year-on-year change available.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ width: '100%', height: 320 }}>
            {/* Use a simple clustered column chart via Recharts BarChart to mirror Syft layout */}
            <ResponsiveContainer>
              <BarChart
                data={netIncomeData && netIncomeData.length ? netIncomeData : [{ month: '—', current: 0, prior: 0 }]}
                barCategoryGap="35%"
                barGap={4}
              >
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#4B5563', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  domain={netIncomeDomain}
                  tickFormatter={(value) =>
                    value === 0 ? '0' : `${(value / 1_000_000).toFixed(0)}M`
                  }
                  tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                <Tooltip
                  formatter={(val: number, name: string) => [
                    formatNetIncomeAmount(val),
                    name === 'current' ? 'Current' : name === 'prior' ? 'Prior' : name,
                  ]}
                />
                <Legend
                  formatter={(value) => (
                    <span style={{ fontFamily: 'Lexend Deca', fontSize: 12, color: '#4B5563' }}>
                      {value}
                    </span>
                  )}
                  payload={[
                    { value: 'Current', type: 'square', id: 'current', color: '#0052CC' },
                    { value: 'Prior', type: 'square', id: 'prior', color: '#9BD5FF' },
                  ]}
                />
                <Bar dataKey="current" name="Current" fill="#0052CC" barSize={16} />
                <Bar dataKey="prior" name="Prior" fill="#9BD5FF" barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Total income comparison – matches Xero Business Snapshot Total income widget */}
      {xeroConnected && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-sm font-medium text-gray-900 mb-1">Total income</h2>
                <p className="text-xs text-gray-500">
                  {netIncomeRangeLabel || 'Select a period to view current vs prior year.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-8">
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Current period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {totalIncomeData
                    ? formatNetIncomeAmount(totalIncomeCurrentTotal)
                    : '—'}
                </span>
                {netIncomeCurrentMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomeCurrentMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Prior period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {totalIncomeData
                    ? formatNetIncomeAmount(totalIncomePriorTotal)
                    : '—'}
                </span>
                {netIncomePriorMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomePriorMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col justify-center min-w-[140px]">
                {totalIncomeData && totalIncomeGrowthPercent != null && totalIncomePriorTotal !== 0 ? (
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: totalIncomeGrowthPercent >= 0 ? '#13AB5B' : '#E93323',
                      fontFamily: 'Lexend Deca',
                    }}
                  >
                    {totalIncomeGrowthPercent >= 0 ? 'Up' : 'Down'}{' '}
                    {Math.abs(totalIncomeGrowthPercent).toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">
                    No year-on-year change available.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <AreaChart
                data={totalIncomeData && totalIncomeData.length ? totalIncomeData : [{ month: '—', current: 0, prior: 0 }]}
              >
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#4B5563', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  domain={totalIncomeDomain}
                  tickFormatter={(value) =>
                    value === 0 ? '0' : `${(value / 1_000_000).toFixed(0)}M`
                  }
                  tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                <Tooltip
                  formatter={(val: number, name: string) => [
                    formatNetIncomeAmount(val),
                    name === 'current' ? 'Current' : name === 'prior' ? 'Prior' : name,
                  ]}
                />
                <Legend
                  formatter={(value) => (
                    <span style={{ fontFamily: 'Lexend Deca', fontSize: 12, color: '#4B5563' }}>
                      {value}
                    </span>
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="current"
                  name="Current"
                  stroke="#0052CC"
                  strokeWidth={2}
                  fill="rgba(0, 82, 204, 0.12)"
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="prior"
                  name="Prior"
                  stroke="#9BD5FF"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  fill="none"
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Total expenses comparison – mirrors Xero Business Snapshot Total expenses widget */}
      {xeroConnected && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-sm font-medium text-gray-900 mb-1">Total expenses</h2>
                <p className="text-xs text-gray-500">
                  {netIncomeRangeLabel || 'Select a period to view current vs prior year.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-8">
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Current period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {totalExpensesData
                    ? formatNetIncomeAmount(totalExpensesCurrentTotal)
                    : '—'}
                </span>
                {netIncomeCurrentMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomeCurrentMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col min-w-[140px]">
                <span className="text-xs text-gray-500 mb-1">Prior period total</span>
                <span className="text-xl font-semibold text-gray-900">
                  {totalExpensesData
                    ? formatNetIncomeAmount(totalExpensesPriorTotal)
                    : '—'}
                </span>
                {netIncomePriorMonthRange && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    Total {netIncomePriorMonthRange}
                  </span>
                )}
              </div>
              <div className="flex flex-col justify-center min-w-[140px]">
                {totalExpensesData && totalExpensesGrowthPercent != null && totalExpensesPriorTotal !== 0 ? (
                  <span
                    className="text-sm font-medium"
                    style={{
                      // For expenses, up = worse (red), down = better (green)
                      color: totalExpensesGrowthPercent >= 0 ? '#E93323' : '#13AB5B',
                      fontFamily: 'Lexend Deca',
                    }}
                  >
                    {totalExpensesGrowthPercent >= 0 ? 'Up' : 'Down'}{' '}
                    {Math.abs(totalExpensesGrowthPercent).toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">
                    No year-on-year change available.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <AreaChart
                data={totalExpensesData && totalExpensesData.length ? totalExpensesData : [{ month: '—', current: 0, prior: 0 }]}
              >
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#4B5563', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickLine={false}
                />
                <YAxis
                  domain={totalExpensesDomain}
                  tickFormatter={(value) =>
                    value === 0 ? '0' : `${(value / 1_000_000).toFixed(0)}M`
                  }
                  tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'Lexend Deca' }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                <Tooltip
                  formatter={(val: number, name: string) => [
                    formatNetIncomeAmount(val),
                    name === 'current' ? 'Current' : name === 'prior' ? 'Prior' : name,
                  ]}
                />
                <Legend
                  formatter={(value) => (
                    <span style={{ fontFamily: 'Lexend Deca', fontSize: 12, color: '#4B5563' }}>
                      {value}
                    </span>
                  )}
                  payload={[
                    { value: 'Current', type: 'square', id: 'current', color: '#0052CC' },
                    { value: 'Prior', type: 'square', id: 'prior', color: '#9BD5FF' },
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="current"
                  name="Current"
                  stroke="#0052CC"
                  strokeWidth={2}
                  fill="rgba(0, 82, 204, 0.12)"
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="prior"
                  name="Prior"
                  stroke="#9BD5FF"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  fill="none"
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {xeroConnected && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-col gap-4 mb-4">
              <div>
                <h2 className="text-sm font-medium text-gray-900 mb-1">Net income margin</h2>
                <p className="text-xs text-gray-500">
                  {netIncomeRangeLabel || 'Select a period to view current vs prior year.'}
                </p>
              </div>
              <div className="flex flex-wrap items-start gap-8">
                <div className="flex flex-col min-w-[120px]">
                  <span className="text-2xl font-semibold text-gray-900">
                    {netProfitMarginResult ? `${Math.round(netProfitMarginDisplayCurrent)}%` : '—'}
                  </span>
                  {netIncomeCurrentMonthRange && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      Average {netIncomeCurrentMonthRange}
                    </span>
                  )}
                </div>
                <div className="flex flex-col min-w-[120px]">
                  <span className="text-2xl font-semibold text-gray-900">
                    {netProfitMarginResult ? `${Math.round(netProfitMarginDisplayPrevious)}%` : '—'}
                  </span>
                  {netIncomePriorMonthRange && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      Average {netIncomePriorMonthRange}
                    </span>
                  )}
                </div>
                <div className="flex flex-col justify-center min-w-[120px]">
                  {netProfitMarginResult && netProfitMarginResult.previousMargin !== 0 ? (
                    <span
                      className="text-sm font-medium"
                      style={{
                        color: netProfitMarginDisplayChange >= 0 ? '#13AB5B' : '#E93323',
                        fontFamily: 'Lexend Deca',
                      }}
                    >
                      {netProfitMarginDisplayChange >= 0 ? 'Up' : 'Down'}{' '}
                      {Math.abs(netProfitMarginDisplayChange).toFixed(2)}%
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      No year-on-year change available.
                    </span>
                  )}
                </div>
              </div>
            </div>
            <XeroStyleTimeSeriesChart
              data={netProfitMarginData ?? []}
              currentColor="#0052CC"
              fillColor="rgba(0, 82, 204, 0.10)"
              currentLabel="Current"
              previousLabel="Prior"
              height={260}
              domain={netProfitMarginDomain}
              yAxisTickFormatter={(value) => `${Math.round(value)}%`}
              valueSuffix="%"
              previousStroke="#9CA3AF"
              previousStrokeDasharray="5 5"
              gridStroke="#E5E7EB"
              zeroLineStroke="#9CA3AF"
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-col gap-4 mb-4">
              <div>
                <h2 className="text-sm font-medium text-gray-900 mb-1">Gross profit margin</h2>
                <p className="text-xs text-gray-500">
                  {netIncomeRangeLabel || 'Select a period to view current vs prior year.'}
                </p>
              </div>
              <div className="flex flex-wrap items-start gap-8">
                <div className="flex flex-col min-w-[120px]">
                  <span className="text-2xl font-semibold text-gray-900">
                    {grossProfitMarginData ? `${Math.round(grossProfitMarginCurrent)}%` : '—'}
                  </span>
                  {netIncomeCurrentMonthRange && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      Average {netIncomeCurrentMonthRange}
                    </span>
                  )}
                </div>
                <div className="flex flex-col min-w-[120px]">
                  <span className="text-2xl font-semibold text-gray-900">
                    {grossProfitMarginData ? `${Math.round(grossProfitMarginPrior)}%` : '—'}
                  </span>
                  {netIncomePriorMonthRange && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      Average {netIncomePriorMonthRange}
                    </span>
                  )}
                </div>
                <div className="flex flex-col justify-center min-w-[120px]">
                  {grossProfitMarginData && grossProfitMarginGrowthPercent != null && grossProfitMarginPrior !== 0 ? (
                    <span
                      className="text-sm font-medium"
                      style={{
                        color: grossProfitMarginGrowthPercent >= 0 ? '#13AB5B' : '#E93323',
                        fontFamily: 'Lexend Deca',
                      }}
                    >
                      {grossProfitMarginGrowthPercent >= 0 ? 'Up' : 'Down'}{' '}
                      {Math.abs(grossProfitMarginGrowthPercent).toFixed(2)}%
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      No year-on-year change available.
                    </span>
                  )}
                </div>
              </div>
            </div>
            <XeroStyleTimeSeriesChart
              data={grossProfitMarginData ?? []}
              currentColor="#0052CC"
              fillColor="rgba(0, 82, 204, 0.10)"
              currentLabel="Current"
              previousLabel="Prior"
              height={260}
              domain={grossProfitMarginDomain}
              yAxisTickFormatter={(value) => `${Math.round(value)}%`}
              valueSuffix="%"
              previousStroke="#9CA3AF"
              previousStrokeDasharray="5 5"
              gridStroke="#E5E7EB"
              zeroLineStroke="#9CA3AF"
            />
          </div>
        </div>
      )}
      {xeroConnected && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div>
              <h2 className="text-sm font-medium text-gray-900 mb-1">Operating Expenses</h2>
              <div className="text-2xl font-semibold text-gray-900">
                {formatNetIncomeAmount(operatingExpensesTotal)}
              </div>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">
                {operatingExpensesLabel || (netIncomeCurrentMonthRange ? `Accounts total ${netIncomeCurrentMonthRange}` : 'Accounts total')}
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start">
              <PieChart width={220} height={220}>
                <Pie
                  data={operatingExpensesData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={44}
                  outerRadius={74}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                >
                  {operatingExpensesData.map((_, idx) => {
                    return <Cell key={idx} fill={operatingExpenseColors[idx % operatingExpenseColors.length]} />
                  })}
                </Pie>
                  <Tooltip content={renderOperatingExpensesTooltip} />
              </PieChart>
              <div className="space-y-2 text-sm w-full">
                {operatingExpensesData.length > 0 ? (
                  operatingExpensesData.map((item, idx) => {
                    return (
                      <div key={item.name} className="flex items-center gap-2 text-gray-700">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: operatingExpenseColors[idx % operatingExpenseColors.length] }}
                        />
                        <span className="truncate">{item.name}</span>
                      </div>
                    )
                  })
                ) : (
                  <span className="text-xs text-gray-500">No operating expense data available.</span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div>
              <h2 className="text-sm font-medium text-gray-900 mb-1">Bank Accounts</h2>
              <div className="text-2xl font-semibold text-gray-900">
                {formatNetIncomeAmount(bankAccountsTotal)}
              </div>
              <p className="text-xs text-gray-500 mt-0.5 mb-4">
                {bankAccountsLabel || (netIncomeCurrentMonthRange ? `Accounts total ${netIncomeCurrentMonthRange}` : 'Accounts total')}
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start">
              <PieChart width={220} height={220}>
                <Pie
                  data={bankAccountsData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={44}
                  outerRadius={74}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                >
                  {bankAccountsData.map((_, idx) => {
                    return <Cell key={idx} fill={bankAccountsColors[idx % bankAccountsColors.length]} />
                  })}
                </Pie>
                <Tooltip content={renderBankAccountsTooltip} />
              </PieChart>
              <div className="space-y-2 text-sm w-full">
                {bankAccountsData.length > 0 ? (
                  bankAccountsData.map((item, idx) => {
                    return (
                      <div key={item.name} className="flex items-center gap-2 text-gray-700">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: bankAccountsColors[idx % bankAccountsColors.length] }}
                        />
                        <span className="truncate">{item.name}</span>
                      </div>
                    )
                  })
                ) : (
                  <span className="text-xs text-gray-500">No bank account data available.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {xeroConnected && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-sm font-medium text-gray-900 mb-1">Profit and loss</h2>
            <div className="text-2xl font-semibold text-gray-900">
              {formatNetIncomeAmount(profitLossTotal)}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {netIncomeCurrentMonthRange ? `Profit/loss ${netIncomeCurrentMonthRange}` : 'Profit/loss'}
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={profitLossChartData.length ? profitLossChartData : [{ month: '—', profit: 0, loss: 0, net: 0 }]}
              barCategoryGap="28%"
              barGap={2}
            >
              <CartesianGrid stroke="#E5E7EB" horizontal vertical={false} />
              <XAxis
                dataKey="month"
                stroke="#6B7280"
                axisLine={{ stroke: '#E9E9EA', strokeWidth: 1 }}
                tickLine={false}
              />
              <YAxis
                stroke="#6B7280"
                axisLine={false}
                tickLine={false}
                tickFormatter={formatMillionsTick}
                domain={profitLossDomain}
              />
              <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
              <Tooltip
                formatter={(value: number, name: string) => [formatNetIncomeAmount(value), name]}
                contentStyle={{ fontFamily: 'Lexend Deca', fontSize: 12 }}
              />
              <Legend />
              <Bar dataKey="profit" name="Profit" fill="#0074C8" barSize={16} />
              <Bar dataKey="loss" name="Loss" fill="#9BD5FF" barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      </>
      )}

      {/* Open in Xero (Syft) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-medium text-gray-900 mb-2">Open Xero Analytics (Syft)</h2>
        <p className="text-sm text-gray-500 mb-4">
          Syft is built into Xero. Open Xero in a new tab to use interactive dashboards, cash flow forecasting, and full Analytics powered by Syft.
        </p>
        <a
          href={XERO_ANALYTICS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#13B5EA' }}
        >
          <FiExternalLink className="w-4 h-4" />
          Open Xero (Analytics)
        </a>
      </div>

      {/* Key reports from Xero API */}
      {xeroConnected && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-base font-medium text-gray-900 mb-4">Key reports from Xero</h2>
          <p className="text-sm text-gray-500 mb-4">
            These reports are fetched from Xero’s Reports API (same data that feeds Syft). Choose a report and optional date range.
          </p>
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Report</label>
              <select
                value={selectedReport}
                onChange={(e) => {
                  setSelectedReport(e.target.value as XeroReportType | '')
                  setReportData(null)
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px]"
              >
                <option value="">Select report…</option>
                {REPORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From date (optional)</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To date (optional)</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={loadReport}
              disabled={!selectedReport || reportLoading}
              className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#212126' }}
            >
              {reportLoading ? 'Loading…' : 'Load report'}
            </button>
          </div>
          {reportData && (
            <>
              {reportData.ReportName && (
                <h3 className="text-sm font-medium text-gray-700 mt-4">{reportData.ReportName}</h3>
              )}
              {reportData.ReportDate && (
                <p className="text-xs text-gray-500">{reportData.ReportDate}</p>
              )}
              {reportData.Rows && reportData.Rows.length > 0 && renderReportTable(reportData)}
              {reportData.Fields && reportData.Fields.length > 0 && renderReportFields(reportData)}
            </>
          )}
        </div>
      )}
    </div>
  )
}

